import { useMemo, ChangeEvent } from "react";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { WorkflowTemplate } from "@idw-types/data/reference-data/generic";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { WorkflowTemplateSelectFormGroup } from "../../../common/select-form-groups";
import { CommonWorkflowConfigurationSection } from "../common-workflow-configuration-section/common-workflow-configuration-section";
import styles from "../add-verification-workflow-modal.less";

type CreateWorkflowFromTemplateSectionProps = {
    axnCompanies: Array<AxnCompany>;
    canAddRelyingPartyDueToLimits: boolean;
    canAddNewRelyingParty: boolean;
    availableRelyingParties: Array<AxnRelyingParty>;
    workflowTemplates: Array<WorkflowTemplate>;
    workflowName: string;
    selectedWorkflowOrganizationOption: SelectOption;
    selectedWorkflowRelyingPartyOption: SelectOption;
    selectedWorkflowTemplateOption: SelectOption;
    workflowRedirectUrls: Array<string>;
    labelWidth: LabelWidthTypes;
    requestInProgress: boolean;
    isLoadingRequiredData: boolean;
    handleUpdateWorkflowTemplate: (newOption: SelectOption) => void;
    onAddRelyingPartySuccess: (newAxnRelyingParty: AxnRelyingParty) => void;
    handleUpdateWorkflowName: (event: ChangeEvent<HTMLInputElement>) => void;
    handleUpdateWorkflowOrganization: (newOption: SelectOption) => void;
    handleUpdateWorkflowRelyingParty: (newOption: SelectOption) => void;
    handleUpdateWorkflowRedirectUrl: (event: ChangeEvent<HTMLInputElement>, index: number) => void;
    onClickAddRedirectUrl: () => void;
    removeWorkflowRedirectUrl: (index: number) => void;
};

export const CreateWorkflowFromTemplateSection = (props: CreateWorkflowFromTemplateSectionProps) => {
    const {
        axnCompanies,
        canAddRelyingPartyDueToLimits,
        canAddNewRelyingParty,
        availableRelyingParties,
        workflowTemplates,
        workflowName,
        selectedWorkflowOrganizationOption,
        selectedWorkflowRelyingPartyOption,
        selectedWorkflowTemplateOption,
        workflowRedirectUrls,
        labelWidth,
        requestInProgress,
        isLoadingRequiredData,
        handleUpdateWorkflowTemplate,
        onAddRelyingPartySuccess,
        handleUpdateWorkflowName,
        handleUpdateWorkflowOrganization,
        handleUpdateWorkflowRelyingParty,
        handleUpdateWorkflowRedirectUrl,
        onClickAddRedirectUrl,
        removeWorkflowRedirectUrl
    } = props;

    const selectedWorkflowTemplate = useMemo(() => {
        if (selectedWorkflowTemplateOption) {
            return workflowTemplates.find(wt => wt.id === selectedWorkflowTemplateOption.value)
        }
        return null;
    }, [selectedWorkflowTemplateOption, workflowTemplates]);

    return (
        <div>
            <WorkflowTemplateSelectFormGroup
                idPrefix="workflow"
                onChange={handleUpdateWorkflowTemplate}
                workflowTemplates={workflowTemplates}
                selectedOption={selectedWorkflowTemplateOption}
                disabled={requestInProgress || isLoadingRequiredData}
                isLoading={isLoadingRequiredData}
                isRequired={true}
                isSearchable={false}
                isClearable={false}
                labelWidth={labelWidth}
            />
            {selectedWorkflowTemplate?.descriptionURL && (
                <div className={styles.documentation}>
                    <a target="_blank" rel="noreferrer" href={selectedWorkflowTemplate.descriptionURL}>
                        {resources.modals.addVerificationWorkflow.readDocumentation}
                    </a>
                </div>
            )}
            <hr />
            <CommonWorkflowConfigurationSection
                axnCompanies={axnCompanies}
                canAddNewRelyingParty={canAddNewRelyingParty}
                canAddRelyingPartyDueToLimits={canAddRelyingPartyDueToLimits}
                availableRelyingParties={availableRelyingParties}
                workflowName={workflowName}
                selectedWorkflowOrganizationOption={selectedWorkflowOrganizationOption}
                selectedWorkflowRelyingPartyOption={selectedWorkflowRelyingPartyOption}
                workflowRedirectUrls={workflowRedirectUrls}
                onAddRelyingPartySuccess={onAddRelyingPartySuccess}
                handleUpdateWorkflowName={handleUpdateWorkflowName}
                handleUpdateWorkflowOrganization={handleUpdateWorkflowOrganization}
                handleUpdateWorkflowRelyingParty={handleUpdateWorkflowRelyingParty}
                handleUpdateWorkflowRedirectUrl={handleUpdateWorkflowRedirectUrl}
                onClickAddRedirectUrl={onClickAddRedirectUrl}
                removeWorkflowRedirectUrl={removeWorkflowRedirectUrl}
                labelWidth={labelWidth}
                requestInProgress={requestInProgress}
            />
        </div>
    );
};